import {LOAD_DATA, SUCCESS, START, ITEM} from '../../../constants'
import {appName} from '../config'
import {createSelector} from 'reselect'
import threeDSettings from '../../../utils/threejs/three-d-settings'
import delay from '../../../utils/delay'
import trackEvent from '../../../utils/track-event'

export const moduleName = 'product'
const prefix = `${appName}/${moduleName}`

export const CHANGE_OPTION_START = `${prefix}/CHANGE_OPTION_START`
export const CHANGE_OPTION_SUCCESS = `${prefix}/CHANGE_OPTION_SUCCESS`
export const CHANGE_OPTION_FAIL = `${prefix}/CHANGE_OPTION_FAIL`

export const CHANGE_QUANTITY_START = `${prefix}/SET_QUANTITY`
export const CHANGE_QUANTITY_SUCCESS = `${prefix}/SET_QUANTITY_SUCCESS`
export const CHANGE_QUANTITY_FAIL = `${prefix}/SET_QUANTITY_FAIL`

export const ADD_TO_CART_START = `${prefix}/ADD_TO_CART_START`
export const ADD_TO_CART_SUCCESS = `${prefix}/ADD_TO_CART_SUCCESS`
export const ADD_TO_CART_FAIL = `${prefix}/ADD_TO_CART_FAIL`

export const GET_THREE_D_VIEWER_START = `${prefix}/GET_THREE_D_VIEWER_START`
export const GET_THREE_D_VIEWER_SUCCESS = `${prefix}/GET_THREE_D_VIEWER_SUCCESS`
export const GET_THREE_D_VIEWER_FAIL = `${prefix}/GET_THREE_D_VIEWER_FAIL`

export const GET_VIEWS_START = `${prefix}/GET_VIEWS_START`
export const GET_VIEWS_SUCCESS = `${prefix}/GET_VIEWS_SUCCESS`
export const GET_VIEWS_FAIL = `${prefix}/GET_VIEWS_FAIL`
export const GET_VIEWS_POLL = `${prefix}/GET_VIEWS_POLL`

const defaultProduct = {
    id: 0,
    condition: {
        options: [],
    },
    options: [],
    views: {},
    neighboring: {items: {}},
    sellableId: 0,
}

export default function reducer(state = defaultProduct, action) {
    const {type, payload, error, response} = action

    switch (type) {
        case ITEM + LOAD_DATA + START:
            return defaultProduct

        case ITEM + LOAD_DATA + SUCCESS:
            return response.product

        case CHANGE_OPTION_START:
            return {
                ...state,
                condition: {
                    ...state.condition,
                    options: {
                        ...state.condition.options,
                        [payload.name]: payload.value,
                    },
                },
                loading: true,
            }
        case CHANGE_OPTION_SUCCESS:
            return {
                ...state,
                condition: {
                    ...state.condition,
                    price: payload.price,
                    options: {
                        ...state.condition.options,
                        [payload.name]: payload.value,
                    },
                },
                views: {
                    ...state.views,
                    large: payload.views[1],
                    small: payload.views[2],
                },
                circulations: payload.circulations,
                loading: false,
            }

        case GET_VIEWS_FAIL:
        case CHANGE_OPTION_FAIL:
        case ADD_TO_CART_FAIL:
        case CHANGE_QUANTITY_FAIL:
            return {
                ...state,
                error,
                loading: false,
            }

        case GET_THREE_D_VIEWER_START:
        case ADD_TO_CART_START:
            return {
                ...state,
                loading: true,
            }

        case CHANGE_QUANTITY_START:
            return {
                ...state,
                condition: {
                    ...state.condition,
                    quantity: payload,
                },
                loading: true,
            }

        case GET_THREE_D_VIEWER_SUCCESS:
        case ADD_TO_CART_SUCCESS:
            return {
                ...state,
                loading: false,
            }

        case GET_VIEWS_SUCCESS:
            return {
                ...state,
                views: {
                    ...state.views,
                    large: payload.views[1],
                    small: payload.views[2],
                },
                ready: true,
            }

        case CHANGE_QUANTITY_SUCCESS:
            return {
                ...state,
                condition: {
                    ...state.condition,
                    price: payload,
                },
                loading: false,
            }

        default:
            return state
    }
}

/**
 * Selectors
 * */

export const productSelector = (state) => ({
    ...state[moduleName],
    condition: {
        ...state[moduleName].condition,
        disabled: isProductDisabled(state),
    },
    options: getCurrentAvaibleOptions(state),
})
export const viewsSelector = (state) => state[moduleName].views
export const editPathSelector = (state) => state[moduleName].editPath
export const productLoadingSelector = (state) => state[moduleName].loading
export const conditionSelector = (state) => state[moduleName].condition
export const optionsSelector = (state) => state[moduleName].options
export const stockMapSelector = (state) => state[moduleName].stock
export const stockSelector = createSelector(stockMapSelector, (stock) =>
    Array.isArray(stock) ? stock.map((item) => ({...item[0]})) : stock
)
export const authorSelector = (state) => state[moduleName].author
export const tagsSelector = (state) => state[moduleName].tags
export const clonesSelector = (state) => state[moduleName].clones
export const singularSelector = (state) => state[moduleName].singular
export const siblingProductsSelector = (state) =>
    state[moduleName].siblingProducts
export const show3dSelector = (state) => state[moduleName].show3d
export const productReadySelector = (state) => state[moduleName].ready
export const productDisabledSelector = (state) => state[moduleName].disabled
export const productBannedSelector = (state) => state[moduleName].banned
export const productShutterstockAuthorSelector = (state) =>
    state[moduleName].shutterStockAuthor

const isOptionDisabled = ({name, value}, currentOptions, stock) => {
    const matches = stock
        .filter((item) => item[name] === value) // get all matches
        .reduce((acc, item) => {
            const cloneItem = {...item}
            delete cloneItem[name]
            return [...acc, {...cloneItem}]
        }, []) // remove the name field from matches

    return matches.some((match) => {
        return Object.keys(match).every(
            (key) => currentOptions[key] === match[key]
        )
    })
}

const isProductDisabled = (state) => {
    const currentOptions = conditionSelector(state).options
    const productDisabled = productDisabledSelector(state)
    const productBanned = productBannedSelector(state)
    const stock = stockSelector(state)
    return (
        productBanned ||
        productDisabled ||
        (Array.isArray(stock)
            ? stock.some((match) => {
                  return Object.keys(match).every(
                      (key) => currentOptions[key] === match[key]
                  )
              })
            : stock < 0)
    )
}

const getCurrentAvaibleOptions = (state) => {
    const options = optionsSelector(state)
    const currentOptions = conditionSelector(state).options
    const stock = stockSelector(state)

    return Array.isArray(options) ? options.map(({name, content}) => {
        return {
            name,
            content: content.map((item) => {
                return {
                    ...item,
                    disabled: isOptionDisabled(
                        {name, value: item.value},
                        currentOptions,
                        stock
                    ),
                }
            }),
        }
    }) : []
}

/**
 * Action Creators
 * */

export const goBack = () => () => {
    if (window.history.length > 1) {
        window.history.back()
    } else {
        window.location = '/'
    }
}

export const watchPrice = () => () => {
    trackEvent({
        category: 'closeup',
        action: 'click',
        label: 'price_control',
    })
    window.subscribePopup.show()
}

const convertOptionsToString = (options) =>
    options
        ? Object.keys(options)
              .map((key) => `${key}:${options[key]}`)
              .join('|')
        : ''

const cacheOfViews = {}
const cacheOfPrice = {}

const isProductReady = (array) => array.every((i) => !!i)

let fetchingCount = 0
const fetchViews = (id, options) => {
    fetchingCount = fetchingCount + 1
    const stringOptions = convertOptionsToString(options)
    if (fetchingCount > 20) {
        return [[], [], []]
    }
    if (cacheOfViews[stringOptions]) {
        return cacheOfViews[stringOptions]
    }
    return fetch(
        `./${id}.json${
            stringOptions ? `?present[variant]=${stringOptions}` : ''
        }`,
        {
            headers: {
                Accept: 'application/json',
            },
            credentials: 'same-origin',
        }
    )
        .then((res) => {
            return res.json()
        })
        .then((json) => {
            if (Object.keys(json).length && isProductReady(json)) {
                cacheOfViews[stringOptions] = json
                return json
            } else {
                return delay(2000).then(() => fetchViews(id, options))
            }
        })
        .catch(() => {
            return delay(2000).then(() => fetchViews(id, options))
        })
}

const getViewsByOptions = async (id, options) => {
    try {
        return await fetchViews(id, options)
    } catch (err) {
        throw err
    }
}

const getPriceByOptions = async (id, quantity, options) => {
    try {
        const stringifiedOptions = convertOptionsToString(options)
        const cacheID = `${quantity}/${stringifiedOptions}`

        if (cacheOfPrice[cacheID]) {
            return cacheOfPrice[cacheID]
        }
        return await fetch(
            `./${id}/current_price.json?quantity=${quantity}&variant=${stringifiedOptions}`,
            {
                headers: {
                    Accept: 'application/json',
                },
                credentials: 'same-origin',
            }
        ).then((res) => {
            const result = res.json()
            cacheOfPrice[cacheID] = result
            return result
        })
    } catch (err) {
        throw err
    }
}

export const changeOption = (name, value) => async (dispatch, getState) => {
    if (value) {
        try {
            dispatch({
                type: CHANGE_OPTION_START,
                payload: {
                    name,
                    value,
                },
            })
            const {
                product: {
                    sellableId,
                    condition: {quantity, options},
                },
            } = getState()
            const views = await getViewsByOptions(sellableId, {
                ...options,
                [name]: value,
            })
            const {
                base_price: basePrice,
                current_price: currentPrice,
                circulations,
            } = await getPriceByOptions(sellableId, quantity, {
                ...options,
                [name]: value,
            })
            dispatch({
                type: CHANGE_OPTION_SUCCESS,
                payload: {
                    name,
                    value,
                    views,
                    price: {
                        basePrice,
                        currentPrice,
                    },
                    circulations: Array.isArray(circulations)
                        ? circulations.map(
                              ({
                                  min_quantity: minQuantity,
                                  price_per_item: pricePerItem,
                              }) => ({
                                  minQuantity,
                                  pricePerItem,
                              })
                          )
                        : null,
                },
            })
        } catch (error) {
            dispatch({
                type: CHANGE_OPTION_FAIL,
                error,
            })
        }
    }
}

export const addToCart = () => (dispatch, getState) => {
    dispatch({
        type: ADD_TO_CART_START,
    })

    const {
        authenticityToken,
        product: {
            id,
            condition: {
                title,
                category,
                options,
                quantity,
                price: {currentPrice},
            },
            sellableId,
            sellableType,
        },
    } = getState()

    const formData = new FormData()

    formData.append('authenticity_token', authenticityToken)
    formData.append('line_item[sellable_id]', sellableId)
    formData.append('line_item[sellable_type]', sellableType)
    formData.append('line_item[quantity]', quantity)
    formData.append(
        'line_item[variant]',
        options ? convertOptionsToString(options) : ''
    )

    window.flocktory = window.flocktory || []
    window.flocktory.push([
        'addToCart',
        {
            item: {
                id,
                price: currentPrice,
                count: 1,
                title,
                categoryId: category,
            },
        },
    ])

    trackEvent({
        category: 'closeup',
        action: 'order',
        label: 'busket',
    })

    fetch('/line_items', {
        method: 'POST',
        body: formData,
        credentials: 'same-origin',
    })
        .then((response) => {
            if (response.status === 200) {
                dispatch({
                    type: ADD_TO_CART_SUCCESS,
                })
                if (window.location.reload) {
                    window.location.reload()
                } else {
                    window.location.href = location.href
                }
            } else {
                throw new Error('Error of response')
            }
        })
        .catch((error) => {
            dispatch({type: ADD_TO_CART_FAIL, error})
        })
}

export const enableThreeDViewer = (element) => async (dispatch, getState) => {
    const {
        product: {singular, arts, condition},
    } = getState()
    dispatch({
        type: GET_THREE_D_VIEWER_START,
    })

    const ThreeJSMediator = (await import('../../../utils/threejs/ThreeJSMediator')).default

    ThreeJSMediator.enable(element)

    ;(async () => {
        if (threeDSettings && threeDSettings[singular]) {
            try {
                await ThreeJSMediator.applySettings({
                    ...threeDSettings[singular],
                    condition: {
                        variants: condition.options,
                    },
                    arts,
                })
                dispatch({
                    type: GET_THREE_D_VIEWER_SUCCESS,
                })
            } catch (err) {
                dispatch({
                    type: GET_THREE_D_VIEWER_FAIL,
                })
                throw err
            }
        }
    })()
}

export const disableThreeDViewer = () => async () => {
    const ThreeJSMediator = (await import('../../../utils/threejs/ThreeJSMediator')).default

    ThreeJSMediator.disable()
}

export const getViews = () => async (dispatch, getState) => {
    const {
        product: {
            sellableId,
            condition: {options},
        },
    } = getState()
    dispatch({
        type: GET_VIEWS_START,
    })

    try {
        dispatch({
            type: GET_VIEWS_POLL,
        })
        const views = await getViewsByOptions(sellableId, options)
        dispatch({
            type: GET_VIEWS_SUCCESS,
            payload: {
                views,
            },
        })
    } catch (error) {
        dispatch({
            type: GET_VIEWS_FAIL,
            error,
        })
    }
}

export const setQuantity = (quantity) => async (dispatch, getState) => {
    dispatch({type: CHANGE_QUANTITY_START, payload: quantity})
    const {
        product: {
            sellableId,
            condition: {options},
        },
    } = getState()
    try {
        const {
            base_price: basePrice,
            current_price: currentPrice,
        } = await getPriceByOptions(sellableId, quantity, options)
        dispatch({
            type: CHANGE_QUANTITY_SUCCESS,
            payload: {basePrice, currentPrice},
        })
    } catch (error) {
        dispatch({type: CHANGE_QUANTITY_FAIL, error})
    }
}
